/**
 * CoachingAlertsCard — instructor-facing panel summarising repeat weaknesses
 * across all pupils. Each row has a one-tap "Plan next lesson" action that
 * pre-fills the pupil's next_lesson_focus with the streak skill.
 * Built for: Consecutive Weak Area Alerts (Tier 1, Apr 2026).
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, TrendingDown, ChevronRight, Check, Loader2, Target } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_META = {
  critical:   { label: "Critical",  color: "text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300" },
  persistent: { label: "Persistent", color: "text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300" },
  emerging:   { label: "Emerging",  color: "text-blue-700 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300" },
};

export function CoachingAlertsCard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planning, setPlanning] = useState(null); // pupil_id currently submitting

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/weak-areas/summary`, { withCredentials: true });
      setData(d);
    } catch { /* noop */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const planFocus = async (pupilId, skill, pupilName) => {
    setPlanning(pupilId);
    try {
      await axios.post(`${API}/api/weak-areas/pupil/${pupilId}/plan-focus`,
        { skills: [skill], mode: "replace" }, { withCredentials: true });
      toast.success(`Next lesson for ${pupilName || "pupil"}: ${skill}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not plan focus");
    }
    setPlanning(null);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        <span className="text-xs text-slate-500">Loading coaching alerts…</span>
      </div>
    );
  }

  const alerts = data?.pupil_alerts || [];
  if (alerts.length === 0) {
    return (
      <div data-testid="coaching-alerts-empty" className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-4 flex items-center gap-3">
        <Check className="w-5 h-5 text-emerald-600" />
        <div>
          <p className="text-sm font-bold text-emerald-800 dark:text-emerald-200">No repeat weaknesses</p>
          <p className="text-[11px] text-emerald-700 dark:text-emerald-300">All pupils are progressing across skill areas.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="coaching-alerts-card" className="bg-white dark:bg-slate-800 rounded-2xl border border-amber-200 dark:border-amber-800 p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <p className="text-xs font-extrabold tracking-wider text-amber-600 uppercase">Coaching Alerts</p>
            <p className="text-[10px] text-slate-500 dark:text-slate-400">{alerts.length} pupil{alerts.length === 1 ? "" : "s"} with repeat weak areas</p>
          </div>
        </div>
      </div>

      {data?.top_skills?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {data.top_skills.map(s => (
            <span key={s.skill} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
              <TrendingDown className="w-2.5 h-2.5 inline mr-0.5 text-red-500" />
              {s.skill} <span className="text-slate-400">× {s.pupil_count}</span>
            </span>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {alerts.slice(0, 4).map(a => {
          const meta = SEVERITY_META[a.top_streak.severity] || SEVERITY_META.emerging;
          const isPlanning = planning === a.pupil_id;
          return (
            <div key={a.pupil_id} data-testid={`alert-${a.pupil_id}`}
              className="bg-slate-50 dark:bg-slate-900 rounded-xl p-2.5 space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[10px] font-bold"
                  style={{ backgroundColor: a.avatar_color || "#6366f1" }}>
                  {(a.pupil_name || "?").trim().split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-900 dark:text-white truncate">
                    {a.pupil_name || "Pupil"}
                  </p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                    {a.top_streak.skill} · seen {a.top_streak.count}×
                  </p>
                </div>
                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${meta.color}`}>
                  {meta.label}
                </span>
              </div>
              <button
                data-testid={`plan-focus-${a.pupil_id}`}
                onClick={() => planFocus(a.pupil_id, a.top_streak.skill, a.pupil_name)}
                disabled={isPlanning}
                className="w-full flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-[11px] font-bold py-1.5 rounded-lg transition active:scale-[0.98]">
                {isPlanning ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <><Target className="w-3 h-3" /> Plan next lesson around {a.top_streak.skill}</>
                )}
              </button>
            </div>
          );
        })}
        {alerts.length > 4 && (
          <button data-testid="alerts-see-all" onClick={() => nav("/instructor-overview")}
            className="w-full text-center text-[11px] font-bold text-blue-600 hover:underline py-1.5">
            See all {alerts.length} pupils →
          </button>
        )}
      </div>
    </div>
  );
}
